import Draggable20 from '@carbon/icons-react/es/draggable/20';
import { Tooltip, Button } from 'antd';
import { TableProps } from 'antd/es/table';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  arrayMove,
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import styled from 'styled-components';
import styles from './draggable-table.module.css';
import { Table } from './table';

const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableWrapper = SortableContainer((props) => <tbody {...props} />);

const DraggableHandle = styled(Button)`
  cursor: grab;
`;

const DragHandle = SortableHandle(() => (
  <Tooltip title={<FormattedMessage id="drag_reorder" />}>
    <DraggableHandle
      type="text"
      icon={<Draggable20 style={{ color: 'var(--icon-secondary)' }} />}
    />
  </Tooltip>
));

const DragHandleCell = {
  dataIndex: 'sort',
  width: 40,
  className: 'drag-visible',
  render: () => <DragHandle />,
};

interface DraggableTableProps<T = any> extends Omit<TableProps<T>, 'rowKey'> {
  onSortChange?: (data: T, updatedRow?: T, updatedRowOrder?: number) => void;
  rowKey: string;
}

export const DraggableTable: FC<DraggableTableProps> = ({
  rowKey,
  columns,
  dataSource = [],
  onSortChange,
  ...rest
}) => {
  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const updatedRow = dataSource[oldIndex];
      const newData = arrayMove<any>(
        [...dataSource],
        oldIndex,
        newIndex,
      ).filter((el) => !!el);

      onSortChange?.(newData, updatedRow, newIndex);
    }
  };

  const DraggableWrapper = (props) => (
    <SortableWrapper
      useDragHandle
      disableAutoscroll
      helperClass={styles.row_dragging}
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = dataSource?.findIndex(
      (x) => x[rowKey] === restProps['data-row-key'],
    );

    return <SortableItem index={index} {...restProps} />;
  };

  return (
    <Table
      {...rest}
      dataSource={dataSource}
      rowKey={rowKey}
      columns={[DragHandleCell, ...(columns ?? [])]}
      components={{
        body: {
          wrapper: DraggableWrapper,
          row: DraggableBodyRow,
        },
      }}
    />
  );
};
